import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiService } from '../services/api';
import type { UserPreferences } from '../types';

interface User {
  name: string;
  email: string;
  id: string;
}

const EMPTY_PREFS: UserPreferences = {
  user_id: '',
  seen: [],
  suppress_all: false,
  version: 1,
  updated_at: 0,
};

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  /** Per-user contextual-help state. Empty until prefs have loaded. */
  prefs: UserPreferences;
  /** Append a card key to `seen`. Idempotent. */
  markSeen: (key: string) => Promise<void>;
  /** Toggle the master suppress flag. */
  setSuppressAll: (value: boolean) => Promise<void>;
  /** Clear `seen` and `suppress_all`, so first-time cards fire again. */
  resetTutorial: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  prefs: EMPTY_PREFS,
  markSeen: async () => undefined,
  setSuppressAll: async () => undefined,
  resetTutorial: async () => undefined,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<UserPreferences>(EMPTY_PREFS);

  // PATCH helpers. We optimistically update `prefs` so the UI hides the
  // welcome modal immediately on "Got it"; the server's response then
  // replaces it (in case anything else has changed). No coalescing yet —
  // single-turn user interactions don't need it; revisit when cards land.
  const markSeen = useCallback(async (key: string) => {
    setPrefs(prev => prev.seen.includes(key) ? prev : { ...prev, seen: [...prev.seen, key] });
    try {
      const fresh = await apiService.patchUserPreferences({ markSeen: key });
      setPrefs(fresh);
    } catch (err) {
      console.warn('[AuthProvider] markSeen failed; keeping optimistic state', err);
    }
  }, []);

  const setSuppressAll = useCallback(async (value: boolean) => {
    setPrefs(prev => ({ ...prev, suppress_all: value }));
    try {
      const fresh = await apiService.patchUserPreferences({ suppressAll: value });
      setPrefs(fresh);
    } catch (err) {
      console.warn('[AuthProvider] setSuppressAll failed; keeping optimistic state', err);
    }
  }, []);

  const resetTutorial = useCallback(async () => {
    setPrefs(prev => ({ ...prev, seen: [], suppress_all: false }));
    try {
      const fresh = await apiService.patchUserPreferences({ resetTutorial: true });
      setPrefs(fresh);
    } catch (err) {
      console.warn('[AuthProvider] resetTutorial failed; keeping optimistic state', err);
    }
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;

    // Skip auth check if we're on auth pages - let the backend handle the flow
    if (currentPath.startsWith('/auth/')) {
      console.log('[AuthProvider] On auth page, skipping auth check');
      setIsLoading(false);
      return;
    }

    // Check if we've already tried to authenticate to prevent loops
    const hasAttemptedAuth = sessionStorage.getItem('auth_attempted');

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    console.log('[AuthProvider] Checking authentication...');
    apiService.fetchCurrentUser({ signal: controller.signal })
      .then(async userData => {
        clearTimeout(timeoutId);
        if (cancelled) return;
        console.log('[AuthProvider] User authenticated:', userData);
        setUser(userData);
        setError(null);
        sessionStorage.removeItem('auth_attempted');
        // Best-effort prefs fetch. If the endpoint is missing (older
        // backend) we just keep EMPTY_PREFS so the UI keeps working.
        try {
          const userPrefs = await apiService.fetchUserPreferences({ signal: controller.signal });
          if (!cancelled) setPrefs(userPrefs);
        } catch (prefsErr) {
          console.warn('[AuthProvider] Failed to load user preferences', prefsErr);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })
      .catch((err: any) => {
        clearTimeout(timeoutId);
        if (cancelled) return;

        if (err?.code === 'ECONNABORTED' || err?.code === 'ERR_CANCELED' || err?.message?.includes('aborted')) {
          console.log('[AuthProvider] Request timed out or aborted');
          setError('Server is not responding. It may be busy processing a job, not running, or unreachable.');
          setIsLoading(false);
          return;
        }

        console.error('[AuthProvider] Authentication check failed:', err);
        const status = err?.response?.status;
        console.log('[AuthProvider] Error status:', status);

        // No response at all — server is down or unreachable
        if (!err?.response) {
          console.error('[AuthProvider] No response from server');
          setError('Cannot connect to the server. It may be busy processing a job, not running, or unreachable.');
          setIsLoading(false);
        // If endpoint returns 404, authentication is likely disabled
        } else if (status === 404) {
          console.log('[AuthProvider] Auth endpoint not found - assuming auth is disabled');
          setUser({ name: 'Guest User', email: 'guest@local', id: 'guest' });
          setError(null);
          // Same best-effort prefs fetch as the authenticated path.
          apiService.fetchUserPreferences()
            .then(p => { if (!cancelled) setPrefs(p); })
            .catch(prefsErr => console.warn('[AuthProvider] Failed to load guest preferences', prefsErr))
            .finally(() => { if (!cancelled) setIsLoading(false); });
        } else if (status === 401) {
          console.log('[AuthProvider] Not authenticated');

          // Prevent infinite redirect loop
          if (hasAttemptedAuth === 'true') {
            console.error('[AuthProvider] Auth loop detected - stopping redirect');
            setError('Authentication failed. Please try refreshing the page or contact your administrator.');
            setIsLoading(false);
            return;
          }

          console.log('[AuthProvider] Redirecting to login...');
          sessionStorage.setItem('auth_attempted', 'true');
          const returnPath = encodeURIComponent(window.location.hash || '#/datasets');
          window.location.href = `/auth/login?return_path=${returnPath}`;
        } else {
          // Other errors - show error but don't redirect
          console.error('[AuthProvider] Unexpected auth error:', err);
          setError('Failed to verify authentication. Please refresh the page.');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    error,
    prefs,
    markSeen,
    setSuppressAll,
    resetTutorial,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
